import { useTranslation } from 'react-i18next'
import { useRankingPersonEvents } from '@/api/queries/scoring-queries'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { ROLE_LABELS } from '@/constants/role-labels'
import { cn } from '@/lib/utils'
import type { RankingEventType, RankingPersonEventsParams } from '@/types'

function formatScore(value: number) {
	return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

// YYYY-MM-DD sem passar por Date, para não deslizar de dia pelo fuso
function formatDay(isoDate: string) {
	const [year, month, day] = isoDate.split('-')
	return `${day}/${month}/${year}`
}

export function RankingPersonDetailsDialog({
	personId,
	params,
	onClose,
}: {
	/** Pessoa aberta no modal; undefined = fechado */
	personId: string | undefined
	params: RankingPersonEventsParams
	onClose: () => void
}) {
	const { t } = useTranslation()
	const { data, isLoading, isError } = useRankingPersonEvents(personId, params)

	const typeLabel = (type: RankingEventType, fallback: string) =>
		t(`administration.scoring.ranking.details.eventTypes.${type}`, {
			defaultValue: fallback,
		})

	return (
		<Dialog open={!!personId} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="flex max-h-[90vh] flex-col sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>
						{data?.person.name ??
							t('administration.scoring.ranking.details.title')}
					</DialogTitle>
					{data && (
						<DialogDescription>
							{[data.person.jobRole, data.person.branchName, data.person.crName]
								.filter(Boolean)
								.join(' · ')}
							<br />
							{t('administration.scoring.ranking.details.period', {
								start: formatDay(data.period.rangeStart),
								end: formatDay(data.period.rangeEnd),
							})}
						</DialogDescription>
					)}
				</DialogHeader>

				{isLoading ? (
					<div className="flex justify-center p-6">
						<Spinner className="text-kaefer-medium-grey" />
					</div>
				) : isError || !data ? (
					<p className="p-6 text-center text-sm text-muted-foreground">
						{t('administration.scoring.ranking.details.error')}
					</p>
				) : (
					<div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
						<div className="rounded-lg border border-kaefer-light-silver bg-kaefer-honey-yellow/10 p-3 text-sm text-kaefer-dark-grey">
							<span className="font-semibold tabular-nums">
								{t('administration.scoring.ranking.details.formula', {
									rawScore: formatScore(data.rawScore),
									weight: formatScore(data.weight),
									role: ROLE_LABELS[data.person.role] ?? data.person.role,
									score: formatScore(data.score),
								})}
							</span>
						</div>

						{data.events.length === 0 ? (
							<p className="rounded-lg border border-dashed border-kaefer-light-silver p-6 text-center text-sm text-muted-foreground">
								{t('administration.scoring.ranking.details.empty')}
							</p>
						) : (
							<>
								<section className="flex flex-col gap-2">
									<h3 className="text-sm font-semibold text-kaefer-dark-grey">
										{t('administration.scoring.ranking.details.byType')}
									</h3>
									<div className="rounded-lg border border-kaefer-light-silver">
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>
														{t('administration.scoring.ranking.details.type')}
													</TableHead>
													<TableHead className="text-right">
														{t('administration.scoring.ranking.details.count')}
													</TableHead>
													<TableHead className="text-right">
														{t('administration.scoring.ranking.details.points')}
													</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.byType.map((row) => (
													<TableRow key={row.type}>
														<TableCell>
															{typeLabel(row.type, row.typeLabel)}
														</TableCell>
														<TableCell className="text-right tabular-nums">
															{row.count}
														</TableCell>
														<TableCell className="text-right font-semibold tabular-nums">
															{formatScore(row.points)}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								</section>

								<section className="flex flex-col gap-2">
									<h3 className="text-sm font-semibold text-kaefer-dark-grey">
										{t('administration.scoring.ranking.details.events', {
											count: data.events.length,
										})}
									</h3>
									<div className="rounded-lg border border-kaefer-light-silver">
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead className="w-24">
														{t('administration.scoring.ranking.details.date')}
													</TableHead>
													<TableHead>
														{t('administration.scoring.ranking.details.type')}
													</TableHead>
													<TableHead className="hidden sm:table-cell">
														{t(
															'administration.scoring.ranking.details.description',
														)}
													</TableHead>
													<TableHead className="text-right">
														{t('administration.scoring.ranking.details.points')}
													</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.events.map((event) => (
													<TableRow key={`${event.type}-${event.id}`}>
														<TableCell className="tabular-nums text-xs">
															{formatDay(event.date)}
														</TableCell>
														<TableCell className="whitespace-normal">
															<div>
																{typeLabel(event.type, event.typeLabel)}
															</div>
															{event.description && (
																<div className="text-xs text-muted-foreground sm:hidden">
																	{event.description}
																</div>
															)}
														</TableCell>
														<TableCell className="hidden whitespace-normal text-xs sm:table-cell">
															<div>{event.description}</div>
															{event.detail && (
																<div className="text-muted-foreground">
																	{event.detail}
																</div>
															)}
														</TableCell>
														<TableCell
															className={cn(
																'text-right tabular-nums',
																event.points === 0
																	? 'text-muted-foreground'
																	: 'font-semibold',
															)}
														>
															{formatScore(event.points)}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								</section>
							</>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
